import { GetStaticPropsContext } from 'next';

import { AccreditationPageContent } from '~/features/accreditation/components/accreditation-page-content';
import { mbaAiContent } from '~/features/accreditation/configs/mba-ai-content';
import { PINNED_ELECTIVE_KEYS } from '~/features/accreditation/constants/woolf-display-order';
import { fetchAccreditationCorePrograms } from '~/features/accreditation/controllers/fetch-accreditation-core-programs';
import { woolfApprovedKeys } from '~/features/accreditation/models/woolf-approved-keys';
import { AccreditationMilestonesProps } from '~/features/accreditation/types';
import { buildCorePrograms } from '~/features/accreditation/utils/build-core-programs';
import { orderByPinnedKeys } from '~/features/accreditation/utils/order-by-pinned-keys';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useSessionReplay } from '~/features/session-replay';

export default function MbaAiLandingPage({
  page,
  milestones,
}: {
  page: Page;
  milestones: AccreditationMilestonesProps;
}) {
  useSessionReplay();

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <AccreditationPageContent {...mbaAiContent} milestones={milestones} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/mba-ai-product-management');

  if (!page) {
    return {
      notFound: true,
    };
  }

  // Electives come from the build-time-generated `woolf-approved-keys.json`
  // (CONENG-2587), validated at module load by `woolfApprovedKeys`. The repo only pins their
  // display order. Core (Tier 1) modules are still fetched live from the Woolf Integration
  // API because each core card renders Woolf course metadata (title, workload hours) that
  // the flattened JSON does not carry. If the live core fetch comes back empty, FAIL the
  // build rather than shipping a static page with an empty curriculum section. The upstream
  // Woolf error is logged by the controller before it returns `null`.
  const liveCore = await fetchAccreditationCorePrograms('mba_ai');

  if (!liveCore || liveCore.courses.length === 0) {
    throw new Error(
      'getStaticProps for /mba-ai-product-management: Woolf Integration API returned no core programs ' +
        '(formalDegreeKey=mba_ai). Refusing to ship a static page with no core modules; see server logs for the upstream Woolf error.'
    );
  }

  const electiveKeys = orderByPinnedKeys(woolfApprovedKeys.mba_ai.elective, (key) => key, PINNED_ELECTIVE_KEYS.mba_ai);
  const { corePrograms, woolfToUdacityProgramKeys } = buildCorePrograms('mba_ai', liveCore.courses);

  const milestones: AccreditationMilestonesProps = {
    ...mbaAiContent.milestones,
    electiveKeys,
    corePrograms,
    woolfToUdacityProgramKeys,
  };

  return {
    props: {
      page,
      milestones,
    },
  };
}
